/**
 * useIdleTimer Hook
 *
 * Tracks user activity and triggers callbacks after a period of inactivity.
 * Used for session timeout functionality.
 *
 * Part of: AI Platform v5.5 - Session Management
 */

import { useState, useEffect, useCallback, useRef } from 'react';

interface UseIdleTimerOptions {
  /** Idle timeout in milliseconds (default: 60 minutes) */
  timeout?: number;
  /** Callback when user becomes idle */
  onIdle: () => void;
  /** Callback when activity detected after being idle */
  onActive?: () => void;
  /** Events to track for activity (default: mouse, keyboard, click, scroll, touch) */
  events?: string[];
  /** Whether the timer is enabled (default: true) */
  enabled?: boolean;
}

interface UseIdleTimerReturn {
  /** Whether the user is currently idle */
  isIdle: boolean;
  /** Timestamp of last activity */
  lastActive: number;
  /** Manually reset the timer */
  reset: () => void;
  /** Pause the timer */
  pause: () => void;
  /** Resume the timer */
  resume: () => void;
  /** Whether the timer is currently paused */
  isPaused: boolean;
}

const DEFAULT_TIMEOUT = 60 * 60 * 1000; // 60 minutes
const DEFAULT_EVENTS = [
  'mousemove',
  'keydown',
  'click',
  'scroll',
  'touchstart',
  'mousedown',
  'wheel'
];

export function useIdleTimer({
  timeout = DEFAULT_TIMEOUT,
  onIdle,
  onActive,
  events = DEFAULT_EVENTS,
  enabled = true
}: UseIdleTimerOptions): UseIdleTimerReturn {
  const [isIdle, setIsIdle] = useState(false);
  const [lastActive, setLastActive] = useState(Date.now());
  const [isPaused, setPaused] = useState(false);

  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const onIdleRef = useRef(onIdle);
  const onActiveRef = useRef(onActive);

  // Keep callbacks refs up to date
  useEffect(() => {
    onIdleRef.current = onIdle;
    onActiveRef.current = onActive;
  }, [onIdle, onActive]);

  const clearIdleTimer = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);

  const startIdleTimer = useCallback(() => {
    clearIdleTimer();
    timeoutRef.current = setTimeout(() => {
      setIsIdle(true);
      onIdleRef.current();
    }, timeout);
  }, [timeout, clearIdleTimer]);

  const reset = useCallback(() => {
    const now = Date.now();
    setLastActive(now);

    if (isIdle) {
      setIsIdle(false);
      onActiveRef.current?.();
    }

    if (enabled && !isPaused) {
      startIdleTimer();
    }
  }, [isIdle, enabled, isPaused, startIdleTimer]);

  const pause = useCallback(() => {
    setPaused(true);
    clearIdleTimer();
  }, [clearIdleTimer]);

  const resume = useCallback(() => {
    setPaused(false);
    if (enabled) {
      startIdleTimer();
    }
  }, [enabled, startIdleTimer]);

  // Handle activity events
  const handleActivity = useCallback(() => {
    if (!enabled || isPaused) return;
    reset();
  }, [enabled, isPaused, reset]);

  // Throttled activity handler to prevent excessive resets
  const throttledActivityRef = useRef<NodeJS.Timeout | null>(null);
  const throttledHandleActivity = useCallback(() => {
    if (throttledActivityRef.current) return;

    handleActivity();

    throttledActivityRef.current = setTimeout(() => {
      throttledActivityRef.current = null;
    }, 250); // Throttle to max 4 updates per second
  }, [handleActivity]);

  // Set up event listeners
  useEffect(() => {
    if (!enabled || isPaused) {
      clearIdleTimer();
      return;
    }

    // Start the timer
    startIdleTimer();

    // Add event listeners
    events.forEach(event => {
      window.addEventListener(event, throttledHandleActivity, { passive: true });
    });

    return () => {
      clearIdleTimer();
      events.forEach(event => {
        window.removeEventListener(event, throttledHandleActivity);
      });
      if (throttledActivityRef.current) {
        clearTimeout(throttledActivityRef.current);
      }
    };
  }, [enabled, isPaused, events, startIdleTimer, clearIdleTimer, throttledHandleActivity]);

  // Clean up on unmount
  useEffect(() => {
    return () => {
      clearIdleTimer();
      if (throttledActivityRef.current) {
        clearTimeout(throttledActivityRef.current);
      }
    };
  }, [clearIdleTimer]);

  return {
    isIdle,
    lastActive,
    reset,
    pause,
    resume,
    isPaused
  };
}

export default useIdleTimer;
